import { CompanyGeolocationSite } from '.';
import type { LocationExtentPrecision } from './api-types';

/**
 * PLT-7875: what a reviewer of a site set is told about each site's coordinates, and the stored
 * verdict it is read from.
 *
 * The verdict belongs to the coordinates, not to the site: every path that writes a coordinate pair
 * writes the verdict with it. A pair Upright looked up carries the lookup's verdict, a pair kept from
 * the saved site keeps the saved verdict, and a pair typed in or given by the agent carries none.
 */

/** What the geocoder said about a site's coordinates when Upright looked its address up. */
export type SiteGeocodeVerdict = Pick<CompanyGeolocationSite, 'geocodePrecision' | 'geocodePartialMatch'>;

/** The verdict of coordinates Upright did not look up: typed in, given by the agent, or cleared. */
export const NO_GEOCODE_VERDICT: SiteGeocodeVerdict = { geocodePrecision: null, geocodePartialMatch: null };

/**
 * The verdict of one lookup that returned coordinates. `extent` is optional because the wire can
 * omit it (see `GeocodeSiteLocationResponse`), and then there is no precision to record.
 */
export function geocodeVerdictOf(match: {
  partialMatch: boolean;
  extent?: { precision: LocationExtentPrecision };
}): SiteGeocodeVerdict {
  return { geocodePrecision: match.extent?.precision ?? null, geocodePartialMatch: match.partialMatch };
}

/**
 * The verdict an edit of a saved site leaves it with: the saved one while the edit leaves the saved
 * pair in place, and none once the pair is new or cleared. An edit that restates the saved pair, as
 * an agent echoing the site it read may, keeps the pair and so the verdict on it.
 */
export function verdictAfterEdit(
  saved: Pick<CompanyGeolocationSite, 'latitude' | 'longitude' | 'geocodePrecision' | 'geocodePartialMatch'>,
  edited: { latitude: number | null; longitude: number | null },
): SiteGeocodeVerdict {
  const keepsPair =
    edited.latitude !== null &&
    edited.longitude !== null &&
    edited.latitude === saved.latitude &&
    edited.longitude === saved.longitude;
  return keepsPair
    ? { geocodePrecision: saved.geocodePrecision, geocodePartialMatch: saved.geocodePartialMatch }
    : NO_GEOCODE_VERDICT;
}

export type SiteCoordinateCheck = 'missing' | 'approximate';

/**
 * What a reviewer should know about a site's coordinates, or null when there is nothing to say.
 *  - `missing`: no coordinate pair, so the site is left out of the physical climate risk assessment.
 *  - `approximate`: Upright placed the site from an address that matched a place wider than the finest
 *    hazard grid (a town, a region), or that Google could match only approximately.
 *
 * Coordinates without a verdict are not flagged, and nor is precision `unknown`: a typed coordinate
 * is its author's claim, and a lookup that reported no extent is no evidence either way.
 */
export function siteCoordinateCheck(
  site: Pick<CompanyGeolocationSite, 'latitude' | 'longitude' | 'geocodePrecision' | 'geocodePartialMatch'>,
): SiteCoordinateCheck | null {
  const hasPair =
    site.latitude != null &&
    site.longitude != null &&
    Number.isFinite(site.latitude) &&
    Number.isFinite(site.longitude);
  if (!hasPair) return 'missing';
  if (site.geocodePartialMatch === true || site.geocodePrecision === 'coarse' || site.geocodePrecision === 'area') {
    return 'approximate';
  }
  return null;
}
